import { Button, DecimalInput, Dialog, Label, TextField } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import { useState, type FormEvent, type ReactNode } from "react";

/**
 * Record a price.
 *
 * ==========================================================================================
 * NO FUTURE DATES, AND THIS IS THE ONE PLACE THE RULE IS NOT MERELY TIDINESS
 * ==========================================================================================
 *
 * Current price is whichever snapshot carries the GREATEST date. A price mistyped as 2027 therefore
 * becomes the portfolio's price immediately and outranks every real quote that follows it — for a
 * year, and no later entry can displace it.
 *
 * Backdating stays freely allowed: backfilling what a long-held stock was worth is exactly how a
 * household gets a chart that reaches back before it started using the app.
 *
 * A WRONG AMOUNT NEEDS NO CORRECTION FLOW — record another for the same date and the later entry
 * wins the tie. A wrong DATE is why prices can be deleted at all.
 */

const today = (): string => new Date().toISOString().slice(0, 10);

export interface PriceFormProps {
  open: boolean;
  onClose: () => void;
  symbol: string;
  onSubmit: (input: { price: string; asOfDate: string }) => void;
  pending: boolean;
  error?: string | null;
}

export function PriceForm({
  open,
  onClose,
  symbol,
  onSubmit,
  pending,
  error,
}: PriceFormProps): ReactNode {
  const t = useT();
  const [price, setPrice] = useState("");
  const [asOfDate, setAsOfDate] = useState(today());

  function submit(event: FormEvent): void {
    event.preventDefault();
    onSubmit({ price, asOfDate });
  }

  return (
    <Dialog open={open} onClose={onClose} title={`Update the price of ${symbol}`}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <div>
          <Label htmlFor="price-value">{t("inv.price.heading")}</Label>
          <DecimalInput
            id="price-value"
            scale={4}
            value={price}
            onValueChange={(next) => {
              setPrice(next);
            }}
            placeholder="650.00"
            required
          />
        </div>

        <div>
          <Label htmlFor="price-date">{t("inv.price.asOf")}</Label>
          <TextField
            id="price-date"
            type="date"
            value={asOfDate}
            max={today()}
            onChange={(event) => {
              setAsOfDate(event.target.value);
            }}
            required
          />
          <p className="text-muted mt-1 text-xs">{t("inv.price.backdateHint")}</p>
        </div>

        {error && <p className="text-danger text-sm">{error}</p>}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" variant="primary" disabled={pending}>
            {t("inv.price.save")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
